'use client'

import type { DeviceName } from '@susu/headless-commerce/components/ResponsiveImage/ResponsiveImage.types'
import type {
  Maybe,
  PinpointOverlayIndicator,
} from '@susu/headless-commerce/gql/generated/graphql'
import { useResizeObserver } from '@susu/headless-commerce/hooks/useResizeObserver'
import {
  isDesktopScreen,
  isMobileScreen,
  isTabletScreen,
} from '@susu/headless-commerce/utils/responsiveUtils'
import { isNull } from '@susu/null'
import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import styles from './TextAndImageRowBannerSection.module.scss'

export type ClientPinpointProps = {
  pinpointItem: PinpointOverlayIndicator
}

export default function ClientPinpoint({
  pinpointItem,
}: Readonly<ClientPinpointProps>) {
  const pinpointRef = useRef<HTMLDivElement>(null)
  const pinpointTextRef = useRef<HTMLSpanElement>(null)
  const [device, setDevice] = useState<DeviceName | undefined>(undefined)
  const [isPinpointTextVisible, setIsPinpointTextVisible] =
    useState<boolean>(false)

  const pinpointTextClasses = classNames(
    styles['imagetext-card__pinpoint-text'],
    {
      [styles['imagetext-card__pinpoint-text--visible']]: isPinpointTextVisible,
    },
  )

  const updateDevice = useCallback(() => {
    setDevice(
      isMobileScreen
        ? 'mobile'
        : isTabletScreen
          ? 'tablet'
          : isDesktopScreen
            ? 'desktop'
            : undefined,
    )
  }, [])

  const {
    mobilePinpointPosition,
    tabletPinpointPosition,
    desktopPinpointPosition,
  } = (pinpointItem as PinpointOverlayIndicator) ?? {}

  const stylesMap: Record<DeviceName, Maybe<string> | undefined> = useMemo(
    () => ({
      mobile: mobilePinpointPosition,
      tablet: tabletPinpointPosition,
      desktop: desktopPinpointPosition,
    }),
    [desktopPinpointPosition, mobilePinpointPosition, tabletPinpointPosition],
  )

  useResizeObserver(updateDevice)

  const handlePinpointClick = useCallback(() => {
    if (
      isNull(pinpointTextRef.current) ||
      isNull(pinpointRef.current) ||
      !pinpointItem
    ) {
      return
    }

    setIsPinpointTextVisible(!isPinpointTextVisible)
  }, [isPinpointTextVisible, pinpointItem])

  useEffect(() => {
    if (!device) {
      updateDevice()
    }

    if (
      !pinpointItem ||
      pinpointItem.__typename !== 'PinpointOverlayIndicator'
    ) {
      return
    }

    if (pinpointRef.current) {
      // @ts-expect-error read-only property
      pinpointRef.current.style = stylesMap[device as Device]
    }
  }, [device, pinpointItem, stylesMap, updateDevice])

  if (!pinpointItem || pinpointItem.__typename !== 'PinpointOverlayIndicator') {
    return null
  }

  return (
    <div className={styles['imagetext-card__pinpoint']} ref={pinpointRef}>
      <button
        type="button"
        className={styles['imagetext-card__pinpoint-circle']}
        onClick={handlePinpointClick}
      />
      <span className={pinpointTextClasses} ref={pinpointTextRef}>
        {pinpointItem?.text}
      </span>
    </div>
  )
}
